import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';
import PublicHeader from '../../components/Layout/PublicHeader';
import PublicFooter from '../../components/Layout/PublicFooter';
import { publicationsAPI } from '../../services/api';
import { 
  BookOpen, 
  Download, 
  Eye, 
  Calendar, 
  User, 
  Search,
  Grid,
  List,
  FileText
} from 'lucide-react';

const PublicationsContainer = styled.div`
  min-height: 100vh;
  background-color: ${props => props.theme.colors.background.primary};
  display: flex;
  flex-direction: column;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, ${props => props.theme.colors.primary} 0%, ${props => props.theme.colors.primaryDark} 100%);
  color: ${props => props.theme.colors.white};
  padding: 6rem 2rem 4rem 2rem;
  text-align: center;
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const HeroTitle = styled.h1`
  font-size: ${props => props.theme.typography.fontSize['4xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  font-family: ${props => props.theme.typography.fontFamily.heading};
  margin: 0 0 1rem 0;
`;

const HeroSubtitle = styled.p`
  font-size: ${props => props.theme.typography.fontSize.lg};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  margin: 0;
  opacity: 0.9;
`;

const Section = styled.section`
  padding: 4rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
  flex: 1;
  width: 100%;
`;

const SectionTitle = styled.h2`
  font-size: ${props => props.theme.typography.fontSize['3xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.heading};
  text-align: center;
  margin: 0 0 3rem 0;
`;

const FiltersContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchContainer = styled.div`
  display: flex;
  gap: 1rem;
  flex: 1;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 0.75rem 1rem;
  border: 1px solid ${props => props.theme.colors.border.light};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.fontSize.base};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}20;
  }
`;

const ViewToggle = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ViewButton = styled.button`
  padding: 0.5rem;
  border: 1px solid ${props => props.theme.colors.border.light};
  background-color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.white};
  color: ${props => props.active ? props.theme.colors.white : props.theme.colors.text.primary};
  border-radius: ${props => props.theme.borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  
  &:hover {
    background-color: ${props => props.active ? props.theme.colors.primaryDark : props.theme.colors.background.secondary};
  }
`;

const PublicationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  
  &.list-view {
    grid-template-columns: 1fr;
  }
`;

const PublicationCard = styled(Card)`
  padding: 1.5rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: transform 0.2s ease-in-out;
  
  &:hover {
    transform: translateY(-4px);
  }
`;

const PublicationImage = styled.div`
  width: 100%;
  height: 200px;
  background-color: ${props => props.theme.colors.background.secondary};
  border-radius: ${props => props.theme.borderRadius.md};
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.text.secondary};
`;

const PublicationTitle = styled.h3`
  font-size: ${props => props.theme.typography.fontSize.lg};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.heading};
  margin: 0 0 0.5rem 0;
  line-height: 1.4;
`;

const PublicationMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  margin-bottom: 1rem;
`;

const PublicationDescription = styled.p`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  margin: 0 0 1rem 0;
  line-height: 1.5;
  flex: 1;
`;

const PublicationTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const PublicationTag = styled.span`
  background-color: ${props => props.theme.colors.primary + '20'};
  color: ${props => props.theme.colors.primary};
  padding: 0.25rem 0.5rem;
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const PublicationsPage = () => {
  const { theme } = useTheme();
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch publications from database
  useEffect(() => {
    const fetchPublications = async () => {
      try {
        setLoading(true);
        const response = await publicationsAPI.getAll({ status: 'published' });
        console.log('Fetched publications:', response.data);
        const pubsData = response.data.data || response.data;
        setPublications(Array.isArray(pubsData) ? pubsData : []);
      } catch (error) {
        console.error('Error fetching publications:', error);
        setError('Failed to load publications');
        setPublications([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPublications();
  }, []);

  const filteredPublications = publications.filter(pub =>
    pub.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    pub.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    pub.authors?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (pub.keywords && pub.keywords.some(keyword => keyword.toLowerCase().includes(searchTerm.toLowerCase())))
  );

  return (
    <PublicationsContainer>
        <PublicHeader theme={theme} />
        <HeroSection>
          <HeroContent>
            <HeroTitle>Research Publications</HeroTitle>
            <HeroSubtitle>
              Access our comprehensive collection of research papers, reports, and guides on women's rights and human rights issues.
            </HeroSubtitle>
          </HeroContent>
        </HeroSection>

        <Section>
          <SectionTitle>Publications Library</SectionTitle>
          
          <FiltersContainer>
            <SearchContainer>
              <SearchInput
                type="text"
                placeholder="Search publications..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Button variant="outline" leftIcon={<Search size={16} />}>
                Search
              </Button>
            </SearchContainer>
            
            <ViewToggle>
              <ViewButton
                active={viewMode === 'grid'}
                onClick={() => setViewMode('grid')}
              >
                <Grid size={16} />
              </ViewButton>
              <ViewButton
                active={viewMode === 'list'}
                onClick={() => setViewMode('list')}
              >
                <List size={16} />
              </ViewButton>
            </ViewToggle>
          </FiltersContainer>

          {loading ? (
            <p style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
              Loading publications...
            </p>
          ) : error ? (
            <p style={{ textAlign: 'center', padding: '2rem', color: '#d32f2f' }}>
              {error}
            </p>
          ) : filteredPublications.length === 0 ? (
            <p style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
              No publications found. Try adjusting your search.
            </p>
          ) : (
            <PublicationsGrid className={viewMode === 'list' ? 'list-view' : ''}>
              {filteredPublications.map((publication) => (
                <PublicationCard key={publication.id}>
                  <PublicationImage>
                    {publication.file_type === 'pdf' ? (
                      <FileText size={48} style={{ opacity: 0.5, color: '#d32f2f' }} />
                    ) : publication.file_type === 'docx' || publication.file_type === 'doc' ? (
                      <FileText size={48} style={{ opacity: 0.5, color: '#2b579a' }} />
                    ) : publication.file_type === 'xlsx' || publication.file_type === 'xls' ? (
                      <FileText size={48} style={{ opacity: 0.5, color: '#217346' }} />
                    ) : publication.file_type === 'pptx' || publication.file_type === 'ppt' ? (
                      <FileText size={48} style={{ opacity: 0.5, color: '#d24726' }} />
                    ) : (
                      <BookOpen size={48} style={{ opacity: 0.5 }} />
                    )}
                  </PublicationImage>
                  
                  <PublicationTitle>{publication.title}</PublicationTitle>
                  
                  <PublicationMeta>
                    <span>
                      <Calendar size={16} /> 
                      {publication.publication_date 
                        ? new Date(publication.publication_date).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })
                        : 'Date not available'
                      }
                    </span>
                    <span><User size={16} /> {publication.authors || 'EWHRDN'}</span>
                  </PublicationMeta>
                  
                  <PublicationDescription>
                    {publication.description}
                  </PublicationDescription>
                  
                  <PublicationTags>
                    {publication.keywords && publication.keywords.length > 0 ? (
                      publication.keywords.map((keyword, index) => (
                        <PublicationTag key={index}>{keyword}</PublicationTag>
                      ))
                    ) : (
                      <PublicationTag>{publication.publication_type}</PublicationTag>
                    )}
                  </PublicationTags>
                  
                  <ActionButtons>
                    {publication.allow_preview && publication.file_url && (
                      <Button 
                        variant="outline" 
                        size="small" 
                        leftIcon={<Eye size={16} />}
                        onClick={() => window.open(publicationsAPI.preview(publication.id), '_blank')}
                      >
                        Preview
                      </Button>
                    )}
                    {publication.is_downloadable && publication.file_url ? (
                      <Button 
                        variant="primary" 
                        size="small" 
                        leftIcon={<Download size={16} />}
                        onClick={async () => {
                          try {
                            const response = await publicationsAPI.download(publication.id);
                            const url = window.URL.createObjectURL(response.data);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = `${publication.title}.${publication.file_type || 'pdf'}`;
                            link.click();
                            window.URL.revokeObjectURL(url);
                          } catch (error) {
                            console.error('Download error:', error);
                            alert('Failed to download publication');
                          }
                        }}
                      >
                        Download ({publication.download_count || 0})
                      </Button>
                    ) : (
                      <Button variant="secondary" size="small" disabled>
                        Download Not Available
                      </Button>
                    )}
                  </ActionButtons>
                </PublicationCard>
              ))}
            </PublicationsGrid>
          )}
        </Section>

        <PublicFooter theme={theme} />
      </PublicationsContainer>
  );
};

export default PublicationsPage;
